import {
  FC,
  HTMLAttributes,
  MutableRefObject,
  MouseEvent,
  memo,
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import cn from 'classnames';

import * as Experiments from '@Queries/Experiments';
import { Tab } from '@UI/ButtonTabs';
import Button from '@UI/Button';
import Section from '@Components/Section';
import DynamicPromoCatalog from '@Components/DynamicPromoCatalog';
import ShowroomsMap from '@Components/ShowroomsMap';
import useSearchParams from '@Hooks/useSearchParams';
import useCountryBasedData from '@Hooks/useCountryBasedData';
import useNavigation from '@Navigation/useNavigation';
import { SellPointData } from '@Types/SellPoints';
import IndividualDiscountPopup from '@Promo/templates/IndividualDiscountPopup';
import MainBanner from './elems/MainBanner';
import SellPoints from './elems/SellPoints';
import ShowroomsGallery from './elems/ShowroomsGallery';
import NewShowroomBanner from './elems/NewShowroomBanner';
import { PageShowroomData } from './typings';
import styles from './PageShowroom.module.css';

export interface PageShowRoomProps extends HTMLAttributes<HTMLDivElement> {
  page: PageShowroomData;
}

const PageShowRoom: FC<PageShowRoomProps> = (props) => {
  const { page } = props;
  const { currentSellPoints, categories, map, notice } = page;
  const { pathname, replaceUrl } = useNavigation();
  const refMap = useRef(null);
  const refGallery = useRef<HTMLDivElement>(null);
  const [selectedPoint, setSelectedPoint] = useState<SellPointData>(null);
  const searchParams = useSearchParams();
  const featureFlags = Experiments.useFeatureFlags();
  const showroomId = searchParams.get('showroomId');
  const hasShowrooms = page.page !== 'ShowroomAll';

  const sellPoints: SellPointData[] = useMemo(() => {
    return currentSellPoints.map((sellPoint) => ({
      ...sellPoint,
      coordinates: sellPoint.coordinates.center,
    }));
  }, [currentSellPoints]);

  const tabs = useMemo(() => {
    const result: Tab[] = [];

    sellPoints.forEach((sellPoint) => {
      result.push({ id: sellPoint.id, label: sellPoint.name });
    });

    return result;
  }, [sellPoints]);

  const defaultShowroom = useMemo(
    () => (sellPoints.some((sellPoint) => sellPoint.id === showroomId) ? showroomId : tabs[0]?.id),
    [sellPoints, showroomId, tabs],
  );

  const defaultTab = useCountryBasedData({
    BLR: tabs[0]?.id,
    default: defaultShowroom,
  });

  const [selectedTab, setSelectedTab] = useState(defaultTab);

  const selectedShowroom = useMemo(() => {
    return sellPoints.find((sellPoint) => sellPoint.id === selectedTab);
  }, [sellPoints, selectedTab]);

  const defaultCatalog = useMemo(() => {
    if (!categories) return null;

    return categories.find((category) => category.id === selectedShowroom?.categoryId);
  }, [categories, selectedShowroom]);

  const showroomsGalleryTitle = useCountryBasedData({
    RUS: 'Магазины Диван.ру',
    BLR: 'Магазины Divan.by',
    KAZ: 'Магазины Mebel.kz',
  });

  const needBanner = useMemo(() => {
    return !notice && featureFlags.videoconsultationAvailable;
  }, [featureFlags.videoconsultationAvailable, notice]);

  const scrollToRef = useCallback(
    (ref: MutableRefObject<HTMLDivElement>, behavior?: ScrollBehavior) => {
      if (!ref?.current) return;

      const rect = ref.current.getBoundingClientRect();
      const top = window.scrollY + rect.top - 74;

      window.scrollTo({ top, behavior });
    },
    [],
  );

  const handleChangeTab = useCallback((_e, tab: Tab) => {
    setSelectedTab(tab.id);
  }, []);

  const handleClickMore = useCallback(
    (e: MouseEvent, sellPoint) => {
      scrollToRef(refMap, 'smooth');
      setSelectedPoint(sellPoint);
    },
    [scrollToRef],
  );

  const handleClick = useCallback(() => {
    scrollToRef(refMap, 'smooth');
  }, [scrollToRef]);

  const handleGoFull = useCallback(
    (e: MouseEvent, point: SellPointData) => {
      setSelectedTab(point.id);
      scrollToRef(refGallery, 'smooth');
    },
    [scrollToRef],
  );

  // Update the selected tab when change defaultTab
  useEffect(() => {
    setSelectedTab(defaultTab);
  }, [defaultTab]);

  // Update the URL when change tab
  useEffect(() => {
    if (selectedTab === tabs[0].id) {
      searchParams.delete('showroomId');
    } else {
      searchParams.set('showroomId', selectedTab);
    }

    replaceUrl(`${pathname}?${searchParams.toString()}`);
  }, [pathname, replaceUrl, searchParams, selectedTab, tabs]);

  // Scroll to gallery when open page with shoowroomId
  useEffect(() => {
    if (!showroomId) return;

    setTimeout(() => {
      scrollToRef(refGallery);
    }, 600);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={styles.page} data-testid='page-showroom'>
      <div className={styles.container}>
        {!hasShowrooms && (
          <div className={styles.borderedBlock}>
            <div className={styles.subtitle}>В вашем городе пока нет шоу&#8209;рума</div>
            <div className={styles.description}>
              Мы растем и работаем над расширением присутствия.
            </div>
          </div>
        )}
      </div>
      <IndividualDiscountPopup />

      {notice && (
        <NewShowroomBanner image={notice.banner} title={notice.title} text={notice.text} />
      )}

      {hasShowrooms && <SellPoints sellPoints={sellPoints} onClickMore={handleClickMore} />}

      {hasShowrooms && sellPoints.length > 1 && (
        <div className={cn(styles.container, styles.buttonContainer)}>
          <Button theme='transparent' onClick={handleClick} className={styles.buttonToMap}>
            Смотреть на карте
          </Button>
        </div>
      )}

      {needBanner && (
        <MainBanner
          className={cn(styles.banner, {
            [styles.withShowrooms]: hasShowrooms,
            [styles.withoutShowrooms]: !hasShowrooms,
          })}
        />
      )}

      {hasShowrooms && (
        <div
          className={cn(styles.gallery, {
            [styles.withoutBanner]: !needBanner,
          })}
          ref={refGallery}
        >
          <ShowroomsGallery
            title={showroomsGalleryTitle}
            tabs={tabs}
            selectedTab={selectedTab}
            images={selectedShowroom?.images || []}
            onChangeTab={handleChangeTab}
          />
        </div>
      )}

      {defaultCatalog && hasShowrooms && (
        <Section title='Товары в шоу-руме'>
          <DynamicPromoCatalog
            key={defaultCatalog.translite}
            slug={defaultCatalog.translite}
            analyticsTitle={`Товары в шоу-руме ${defaultCatalog.translite}`}
            className={styles.catalog}
          />
        </Section>
      )}

      {map && (
        <div className={styles.container} ref={refMap}>
          <ShowroomsMap
            className={styles.map}
            map={map}
            selectedPoint={selectedPoint}
            onGoFull={handleGoFull}
          />
        </div>
      )}
    </div>
  );
};

export default memo(PageShowRoom);
